/**
 * Scroll Statement Block - Interactivity API View Script
 *
 * Each line owns a slice of the pinned scroll runway. Across its own slice it
 * rises from 300px below its natural slot into that slot while its opacity
 * goes 0 to 1, then it stays there — lines accumulate rather than handing off,
 * so once a line has landed it never fades again and rests at transform none.
 *
 * The only thing written per frame is one custom property per line, from a
 * single getBoundingClientRect on the host. Nothing is measured while
 * scrolling, so there is no forced reflow in the handler.
 *
 * @package BrandyBlocks
 */

import { store, getElement } from '@wordpress/interactivity';

// Matches WordPress's own mobile breakpoint. Several lines at 100vh each is a
// lot of scroll for a handful of words, which is punishing on a phone.
const MOBILE_MAX = 782;
const STATIC_CLASS = 'brandy-scroll-statement--static';
const RESIZE_DEBOUNCE = 150;

// How early a line starts arriving relative to the one above it landing. The
// block takes no attributes, so this is the single place it is decided.
const OVERLAP = 0.1;

// Spring chasing the scroll-derived target.
//
// Tuned against the reference, which settles in roughly 60-70ms and overshoots
// its slot by about 3.5% of the travel. Here that is a damping ratio near 0.7
// (c / (2 * sqrt(k)) = 42 / 60) giving ~4% overshoot, and a settling time of
// about 4 / (0.7 * sqrt(900)) ≈ 190ms. Softer springs read as lag rather than
// smoothing — an earlier 170/24 pairing was still climbing half a second after
// the scroll had stopped.
const STIFFNESS = 900;
const DAMPING = 42;
const SETTLE_EPSILON = 0.0001;

const clamp = ( value, min, max ) => Math.min( max, Math.max( min, value ) );

function initScrollStatement( host ) {
	const viewport = host.querySelector( '.brandy-scroll-statement__viewport' );
	const track = host.querySelector( '.brandy-scroll-statement__track' );

	if ( ! viewport || ! track ) {
		return;
	}

	const lines = Array.from(
		track.querySelectorAll( ':scope > .brandy-statement-line' )
	);

	const reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

	let rafId = 0;
	let resizeTimer = 0;
	let isStatic = null;

	// Spring state: `current` is what is painted, chasing the scroll target.
	let current = 0;
	let velocity = 0;
	let lastFrame = 0;

	/**
	 * Each line gets a window of the runway. With no overlap the windows tile
	 * it exactly — line i owns [i/n, (i+1)/n] — and raising overlap slides the
	 * starts closer together so a line begins arriving before its predecessor
	 * has landed. Span is solved so the last window still ends at 1.
	 */
	const count = lines.length;
	const span = 1 / ( count - OVERLAP * ( count - 1 ) );
	const stride = span * ( 1 - OVERLAP );

	function clearInlineState() {
		lines.forEach( ( line ) =>
			line.style.removeProperty( '--statement-progress' )
		);
	}

	function shouldBeStatic() {
		return (
			reduceMotion.matches ||
			window.innerWidth <= MOBILE_MAX ||
			count < 2
		);
	}

	/**
	 * Where the scroll position says the animation should be, right now.
	 * Returns null when there is nothing to drive — no runway, or the frame is
	 * nowhere near the viewport.
	 */
	function readTarget() {
		const room = host.offsetHeight - viewport.offsetHeight;
		if ( room <= 0 ) {
			return null;
		}

		const rect = host.getBoundingClientRect();
		if ( rect.bottom < 0 || rect.top > window.innerHeight ) {
			return null;
		}

		return clamp( -rect.top / room, 0, 1 );
	}

	function paint( progress ) {
		for ( let index = 0; index < count; index++ ) {
			const start = index * stride;
			const own = clamp( ( progress - start ) / span, 0, 1 );
			lines[ index ].style.setProperty(
				'--statement-progress',
				String( own )
			);
		}
	}

	/**
	 * Scroll sets a target; a spring chases it. Without this the value is a
	 * direct function of scroll position, so a mouse wheel — which arrives in
	 * discrete jumps — lands as a series of steps rather than a glide.
	 *
	 * Slightly under-damped on purpose: a line drifts a few pixels past its
	 * slot and settles back, which is what the reference does (measured ~11px
	 * of overshoot resolving over about four frames).
	 */
	function step( now ) {
		rafId = 0;

		const target = readTarget();
		if ( target === null ) {
			lastFrame = 0;
			return;
		}

		const dt = lastFrame ? Math.min( ( now - lastFrame ) / 1000, 1 / 30 ) : 1 / 60;
		lastFrame = now;

		const accel = -STIFFNESS * ( current - target ) - DAMPING * velocity;
		velocity += accel * dt;
		current += velocity * dt;

		const settled =
			Math.abs( target - current ) < SETTLE_EPSILON &&
			Math.abs( velocity ) < SETTLE_EPSILON;

		if ( settled ) {
			current = target;
			velocity = 0;
			lastFrame = 0;
		}

		paint( current );

		if ( ! settled ) {
			rafId = requestAnimationFrame( step );
		}
	}

	function kick() {
		if ( ! rafId ) {
			rafId = requestAnimationFrame( step );
		}
	}

	/**
	 * Jump straight to the scroll position with no spring — for first paint and
	 * after a resize, where easing in from a stale value would look like drift.
	 */
	function snap() {
		const target = readTarget();
		if ( target === null ) {
			return;
		}
		current = target;
		velocity = 0;
		lastFrame = 0;
		paint( current );
	}

	/**
	 * Switching modes has to work in both directions — rotating a tablet or
	 * toggling the OS reduced-motion setting can enter or leave static mode.
	 */
	function applyMode() {
		const next = shouldBeStatic();
		if ( next === isStatic ) {
			return;
		}
		isStatic = next;

		if ( next ) {
			host.classList.add( STATIC_CLASS );
			window.removeEventListener( 'scroll', kick );
			cancelAnimationFrame( rafId );
			rafId = 0;
			velocity = 0;
			lastFrame = 0;
			clearInlineState();
			return;
		}

		host.classList.remove( STATIC_CLASS );
		window.addEventListener( 'scroll', kick, { passive: true } );
		snap();
	}

	function onResize() {
		window.clearTimeout( resizeTimer );
		resizeTimer = window.setTimeout( () => {
			applyMode();
			if ( isStatic === false ) {
				snap();
			}
		}, RESIZE_DEBOUNCE );
	}

	window.addEventListener( 'resize', onResize, { passive: true } );
	reduceMotion.addEventListener( 'change', applyMode );

	applyMode();

	return () => {
		window.removeEventListener( 'scroll', kick );
		window.removeEventListener( 'resize', onResize );
		reduceMotion.removeEventListener( 'change', applyMode );
		window.clearTimeout( resizeTimer );
		cancelAnimationFrame( rafId );
	};
}

store( 'brandy/scroll-statement', {
	callbacks: {
		init() {
			const { ref } = getElement();
			return initScrollStatement( ref );
		},
	},
} );
